import React from "react";
import { Box, Typography, Grid, Paper } from "@mui/material";
import { DataGridPro, GridColDef } from "@mui/x-data-grid-pro";
import {
  Log,
  OffensivePlayCall,
  OffensivePlayLog,
  KORPlayCall,
  KORPlayLog,
  DefensivePlayCall,
  DefensivePlayLog,
  KickoffPlayCall,
  KickoffPlayLog,
  PuntPlayCall,
  PuntPlayLog,
  PuntReturnPlayCall,
  PuntReturnPlayLog,
  FieldGoalPlayCall,
  FieldGoalPlayLog,
  FieldGoalBlockPlayCall,
  FieldGoalBlockPlayLog,
  OffensePlayCallLog,
} from "../../../types/LogTypes";
import { theme } from "../../../styles/Theme";

interface PlayDetailsProps {
  log: Log;
}

const OffensivePlayDetails: React.FC<{
  playCall: OffensePlayCallLog;
  playLog: OffensivePlayLog;
}> = ({ playCall, playLog }) => {
  // Define columns for all relevant fields from playCall and playLog
  const columns: GridColDef[] = [
    {
      field: "personnel_group_name",
      headerName: "Personnel",
      width: 120,
      editable: true,
    },
    {
      field: "formation_name",
      headerName: "Formation",
      width: 120,
      editable: true,
    },
    {
      field: "formation_direction",
      headerName: "Formation Direction",
      width: 150,
      editable: true,
    },
    { field: "code_word", headerName: "Code Word", width: 120, editable: true },
    { field: "tag_name", headerName: "Tag", width: 120, editable: true },
    { field: "gain_loss", headerName: "Gain/Loss", width: 100, editable: true },
    { field: "rusher", headerName: "Rusher", width: 120, editable: true },
    { field: "receiver", headerName: "Receiver", width: 120, editable: true },
    { field: "passer", headerName: "Passer", width: 120, editable: true },
    
    { field: "fumble", headerName: "Fumble", width: 200, editable: true },
    {
      field: "interception",
      headerName: "Interception",
      width: 200,
      editable: true,
    },
 

  ];

  // Compose a single row with all relevant data
  const row = {
    id: 1,
    name: playCall?.name ?? "",
    code_word: playCall?.code_word ?? "",
    personnel_group_name: playCall?.personnel_group_name ?? "",
    formation_name: playCall?.formation_name ?? "",
    formation_direction: playCall?.formation_direction ?? "",
    concept_name: playCall?.concept_name ?? "",
    run_concept_name: playCall?.run_concept_name ?? "",
    pass_concept: playCall?.pass_concept ?? "",
    pass_protection_concept: playCall?.pass_protection_concept ?? "",
    tag_name: playCall?.tag_name ?? "",
    gain_loss: playLog?.gain_loss ?? "",
    passer: playLog?.passer?.name ?? "",
    receiver: playLog?.receiver?.name ?? "",
    rusher: playLog?.rusher?.name ?? "",
    fumble: playLog?.fumble
      ? `${playLog.fumble.fumble_player?.name || "Unknown"}${
          playLog.fumble.fumble_recovery_player
            ? ` - Recovered by: ${playLog.fumble.fumble_recovery_player.name}`
            : ""
        }`
      : "",
    interception: playLog?.interception
      ? `${playLog.interception.interception_player?.name || "Unknown"}${
          playLog.interception.passer
            ? ` - Thrown by: ${playLog.interception.passer.name}`
            : ""
        }`
      : "",
  };

  return (
    <Box sx={{ height: 120, width: "100%" }}>
      <DataGridPro
        rows={[row]}
        columns={columns}
        hideFooter
        disableRowSelectionOnClick
        rowHeight={40}
      />
    </Box>
  );
};

const KORPlayDetails: React.FC<{
  playCall: KORPlayCall;
  playLog: KORPlayLog;
}> = ({ playCall, playLog }) => {
  const columns: GridColDef[] = [
    { field: "personnel", headerName: "Personnel", width: 150, editable: true },
    { field: "name", headerName: "Play Name", width: 150, editable: true },
    { field: "returner", headerName: "Returner", width: 150, editable: true },
    {
      field: "return_yardage",
      headerName: "Return Yardage",
      width: 120,
      editable: true,
    },
    {
      field: "return_to_yardline",
      headerName: "Return To Yardline",
      width: 150,
      editable: true,
    },
    {
      field: "kickoff_yardage",
      headerName: "Kickoff Yardage",
      width: 120,
      editable: true,
    },
    {
      field: "kickoff_from",
      headerName: "Kickoff From",
      width: 120,
      editable: true,
    },
    {
      field: "kickoff_to",
      headerName: "Kickoff To",
      width: 120,
      editable: true,
    },
  ];

  const row = {
    id: 1,
    name: playCall?.name ?? "",
    personnel: playCall?.personnel_group?.name ?? "",
    returner: playLog?.returner?.name ?? "",
    kickoff_from: playLog?.kickoff_from ?? "",
    kickoff_to: playLog?.kickoff_to ?? "",
    kickoff_yardage: playLog?.kickoff_yardage ?? "",
    return_to_yardline: playLog?.return_to_yardline ?? "",
    return_yardage: playLog?.return_yardage ?? "",
  };

  return (
    <Box sx={{ height: 120, width: "100%" }}>
      <DataGridPro
        rows={[row]}
        columns={columns}
        hideFooter
        disableRowSelectionOnClick
        rowHeight={60}
      />
    </Box>
  );
};

const DefensivePlayDetails: React.FC<{
  playCall: DefensivePlayCall;
  playLog: DefensivePlayLog;
}> = ({ playCall, playLog }) => (
  <Box sx={{ p: 2 }}>
    <Typography
      variant="h6"
      gutterBottom
      sx={{ color: theme.palette.primary.main }}
    >
      Defensive Play Details
    </Typography>

    <Grid container spacing={2}>
      <Grid item xs={12} md={6}>
        <Paper sx={{ p: 2, bgcolor: "background.default" }}>
          <Typography variant="subtitle1" gutterBottom fontWeight="bold">
            Play Call
          </Typography>
          <Typography>
            <strong>Name:</strong> {playCall.name}
          </Typography>
          <Typography>
            <strong>Personnel:</strong> {playCall.personnel.name}
          </Typography>
        </Paper>
      </Grid>

      <Grid item xs={12} md={6}>
        <Paper sx={{ p: 2, bgcolor: "background.default" }}>
          <Typography variant="subtitle1" gutterBottom fontWeight="bold">
            Play Result
          </Typography>
          <Typography>
            <strong>Gain/Loss Allowed:</strong> {playLog.gain_loss} yards
          </Typography>
          {playLog.tackle && (
            <Typography>
              <strong>Tackle:</strong>{" "}
              {playLog.tackle.tackler?.name || "Unknown"}
              {playLog.tackle.assisted_tackler && (
                <> (Assisted by: {playLog.tackle.assisted_tackler.name})</>
              )}
            </Typography>
          )}
          {playLog.pass_break_up && (
            <Typography>
              <strong>Pass Break Up:</strong> {playLog.pass_break_up.name}
            </Typography>
          )}
          {playLog.fumble && (
            <Typography color="success.main">
              <strong>Fumble Forced:</strong>{" "}
              {playLog.fumble.fumble_player?.name || "Unknown"}
              {playLog.fumble.fumble_recovery_player && (
                <>
                  {" "}
                  - Recovered by: {playLog.fumble.fumble_recovery_player.name}
                </>
              )}
            </Typography>
          )}
          {playLog.interception && (
            <Typography color="success.main">
              <strong>Interception:</strong>{" "}
              {playLog.interception.interception_player?.name || "Unknown"}
              {playLog.interception.passer && (
                <> - Thrown by: {playLog.interception.passer.name}</>
              )}
            </Typography>
          )}
        </Paper>
      </Grid>
    </Grid>
  </Box>
);

const KickoffPlayDetails: React.FC<{
  playCall: KickoffPlayCall;
  playLog: KickoffPlayLog;
}> = ({ playCall, playLog }) => (
  <Box sx={{ p: 2 }}>
    <Typography
      variant="h6"
      gutterBottom
      sx={{ color: theme.palette.primary.main }}
    >
      Kickoff Details
    </Typography>

    <Grid container spacing={2}>
      <Grid item xs={12} md={6}>
        <Paper sx={{ p: 2, bgcolor: "background.default" }}>
          <Typography variant="subtitle1" gutterBottom fontWeight="bold">
            Play Call
          </Typography>
          <Typography>
            <strong>Name:</strong> {playCall.name}
          </Typography>
          <Typography>
            <strong>Personnel:</strong> {playCall.personnel.name}
          </Typography>
        </Paper>
      </Grid>

      <Grid item xs={12} md={6}>
        <Paper sx={{ p: 2, bgcolor: "background.default" }}>
          <Typography variant="subtitle1" gutterBottom fontWeight="bold">
            Play Result
          </Typography>
          <Typography>
            <strong>Kickoff From:</strong> {playLog.kickoffFrom}
          </Typography>
          <Typography>
            <strong>Kickoff To:</strong> {playLog.kickoffTo}
          </Typography>
          <Typography>
            <strong>Kickoff Yardage:</strong> {playLog.kickoffYardage} yards
          </Typography>
          <Typography>
            <strong>Return Yardage:</strong> {playLog.returnYardage} yards
          </Typography>
          {playLog.tackle && (
            <Typography>
              <strong>Tackle:</strong>{" "}
              {playLog.tackle.tackler?.name || "Unknown"}
              {playLog.tackle.assisted_tackler && (
                <> (Assisted by: {playLog.tackle.assisted_tackler.name})</>
              )}
            </Typography>
          )}
          {playLog.fumble && (
            <Typography color="success.main">
              <strong>Fumble Forced:</strong>{" "}
              {playLog.fumble.fumble_player?.name || "Unknown"}
              {playLog.fumble.fumble_recovery_player && (
                <>
                  {" "}
                  - Recovered by: {playLog.fumble.fumble_recovery_player.name}
                </>
              )}
            </Typography>
          )}
        </Paper>
      </Grid>
    </Grid>
  </Box>
);

const PuntPlayDetails: React.FC<{
  playCall: PuntPlayCall;
  playLog: PuntPlayLog;
}> = ({ playCall, playLog }) => (
  <Box sx={{ p: 2 }}>
    <Typography
      variant="h6"
      gutterBottom
      sx={{ color: theme.palette.primary.main }}
    >
      Punt Details
    </Typography>

    <Grid container spacing={2}>
      <Grid item xs={12} md={6}>
        <Paper sx={{ p: 2, bgcolor: "background.default" }}>
          <Typography variant="subtitle1" gutterBottom fontWeight="bold">
            Play Call
          </Typography>
          <Typography>
            <strong>Name:</strong> {playCall.name}
          </Typography>
          <Typography>
            <strong>Personnel:</strong> {playCall.personnel.name}
          </Typography>
        </Paper>
      </Grid>

      <Grid item xs={12} md={6}>
        <Paper sx={{ p: 2, bgcolor: "background.default" }}>
          <Typography variant="subtitle1" gutterBottom fontWeight="bold">
            Play Result
          </Typography>
          {playLog.punter && (
            <Typography>
              <strong>Punter:</strong> {playLog.punter.name}
            </Typography>
          )}
          <Typography>
            <strong>Punt From:</strong> {playLog.puntFrom}
          </Typography>
          <Typography>
            <strong>Punt To:</strong> {playLog.puntTo}
          </Typography>
          <Typography>
            <strong>Punt Yardage:</strong> {playLog.puntYardage} yards
          </Typography>
          {playLog.tackle && (
            <Typography>
              <strong>Tackle:</strong>{" "}
              {playLog.tackle.tackler?.name || "Unknown"}
              {playLog.tackle.assisted_tackler && (
                <> (Assisted by: {playLog.tackle.assisted_tackler.name})</>
              )}
            </Typography>
          )}
          {playLog.fumble && (
            <Typography color="error">
              <strong>Fumble:</strong>{" "}
              {playLog.fumble.fumble_player?.name || "Unknown"}
              {playLog.fumble.fumble_recovery_player && (
                <>
                  {" "}
                  - Recovered by: {playLog.fumble.fumble_recovery_player.name}
                </>
              )}
            </Typography>
          )}
        </Paper>
      </Grid>
    </Grid>
  </Box>
);

const PuntReturnPlayDetails: React.FC<{
  playCall: PuntReturnPlayCall;
  playLog: PuntReturnPlayLog;
}> = ({ playCall, playLog }) => (
  <Box sx={{ p: 2 }}>
    <Typography
      variant="h6"
      gutterBottom
      sx={{ color: theme.palette.primary.main }}
    >
      Punt Return Details
    </Typography>

    <Grid container spacing={2}>
      <Grid item xs={12} md={6}>
        <Paper sx={{ p: 2, bgcolor: "background.default" }}>
          <Typography variant="subtitle1" gutterBottom fontWeight="bold">
            Play Call
          </Typography>
          <Typography>
            <strong>Name:</strong> {playCall.name}
          </Typography>
          <Typography>
            <strong>Personnel:</strong> {playCall.personnel.name}
          </Typography>
        </Paper>
      </Grid>

      <Grid item xs={12} md={6}>
        <Paper sx={{ p: 2, bgcolor: "background.default" }}>
          <Typography variant="subtitle1" gutterBottom fontWeight="bold">
            Play Result
          </Typography>
          {playLog.returner && (
            <Typography>
              <strong>Returner:</strong> {playLog.returner.name}
            </Typography>
          )}
          <Typography>
            <strong>Punt From:</strong> {playLog.puntFrom}
          </Typography>
          <Typography>
            <strong>Punt To:</strong> {playLog.puntTo}
          </Typography>
          <Typography>
            <strong>Punt Yardage:</strong> {playLog.puntYardage} yards
          </Typography>
          <Typography>
            <strong>Return Yardage:</strong> {playLog.returnYardage} yards
          </Typography>
          <Typography>
            <strong>Return To Yardline:</strong> {playLog.returnToYardline}
          </Typography>
          {playLog.fumble && (
            <Typography color="error">
              <strong>Fumble:</strong>{" "}
              {playLog.fumble.fumble_player?.name || "Unknown"}
              {playLog.fumble.fumble_recovery_player && (
                <>
                  {" "}
                  - Recovered by: {playLog.fumble.fumble_recovery_player.name}
                </>
              )}
            </Typography>
          )}
        </Paper>
      </Grid>
    </Grid>
  </Box>
);

const FieldGoalPlayDetails: React.FC<{
  playCall: FieldGoalPlayCall;
  playLog: FieldGoalPlayLog;
}> = ({ playCall, playLog }) => (
  <Box sx={{ p: 2 }}>
    <Typography
      variant="h6"
      gutterBottom
      sx={{ color: theme.palette.primary.main }}
    >
      Field Goal Details
    </Typography>

    <Grid container spacing={2}>
      <Grid item xs={12} md={6}>
        <Paper sx={{ p: 2, bgcolor: "background.default" }}>
          <Typography variant="subtitle1" gutterBottom fontWeight="bold">
            Play Call
          </Typography>
          <Typography>
            <strong>Name:</strong> {playCall.name}
          </Typography>
          <Typography>
            <strong>Personnel:</strong> {playCall.personnel.name}
          </Typography>
        </Paper>
      </Grid>

      <Grid item xs={12} md={6}>
        <Paper sx={{ p: 2, bgcolor: "background.default" }}>
          <Typography variant="subtitle1" gutterBottom fontWeight="bold">
            Play Result
          </Typography>
          {playLog.kicker && (
            <Typography>
              <strong>Kicker:</strong> {playLog.kicker.name}
            </Typography>
          )}
          <Typography>
            <strong>Distance:</strong> {playLog.fieldGoalDistance} yards
          </Typography>
          <Typography>
            <strong>Result:</strong>
            <span
              style={{
                color:
                  playLog.fieldGoalResult === "GOOD"
                    ? "green"
                    : playLog.fieldGoalResult === "BLOCKED"
                    ? "orange"
                    : "red",
              }}
            >
              {playLog.fieldGoalResult}
            </span>
          </Typography>
          {playLog.pat && (
            <Typography>
              <strong>Point After Touchdown:</strong> Yes
            </Typography>
          )}
          {playLog.fumble && (
            <Typography color="error">
              <strong>Fumble:</strong>{" "}
              {playLog.fumble.fumble_player?.name || "Unknown"}
              {playLog.fumble.fumble_recovery_player && (
                <>
                  {" "}
                  - Recovered by: {playLog.fumble.fumble_recovery_player.name}
                </>
              )}
            </Typography>
          )}
          {playLog.interception && (
            <Typography color="error">
              <strong>Interception:</strong>{" "}
              {playLog.interception.interception_player?.name || "Unknown"}
              {playLog.interception.passer && (
                <> - Thrown by: {playLog.interception.passer.name}</>
              )}
            </Typography>
          )}
        </Paper>
      </Grid>
    </Grid>
  </Box>
);

const FieldGoalBlockPlayDetails: React.FC<{
  playCall: FieldGoalBlockPlayCall;
  playLog: FieldGoalBlockPlayLog;
}> = ({ playCall, playLog }) => (
  <Box sx={{ p: 2 }}>
    <Typography
      variant="h6"
      gutterBottom
      sx={{ color: theme.palette.primary.main }}
    >
      Field Goal Block Details
    </Typography>

    <Grid container spacing={2}>
      <Grid item xs={12} md={6}>
        <Paper sx={{ p: 2, bgcolor: "background.default" }}>
          <Typography variant="subtitle1" gutterBottom fontWeight="bold">
            Play Call
          </Typography>
          <Typography>
            <strong>Name:</strong> {playCall.name}
          </Typography>
          <Typography>
            <strong>Personnel:</strong> {playCall.personnel.name}
          </Typography>
        </Paper>
      </Grid>

      <Grid item xs={12} md={6}>
        <Paper sx={{ p: 2, bgcolor: "background.default" }}>
          <Typography variant="subtitle1" gutterBottom fontWeight="bold">
            Play Result
          </Typography>
          {playLog.blockedBy && (
            <Typography color="success.main">
              <strong>Blocked By:</strong> {playLog.blockedBy.name}
            </Typography>
          )}
          <Typography>
            <strong>Field Goal Distance:</strong> {playLog.fieldGoalDistance}{" "}
            yards
          </Typography>
          <Typography>
            <strong>Result:</strong>
            <span
              style={{
                color:
                  playLog.fieldGoalResult === "GOOD"
                    ? "green"
                    : playLog.fieldGoalResult === "BLOCKED"
                    ? "orange"
                    : "red",
              }}
            >
              {playLog.fieldGoalResult}
            </span>
          </Typography>
          {playLog.pat && (
            <Typography>
              <strong>Point After Touchdown:</strong> Yes
            </Typography>
          )}
          {playLog.returnYardage && playLog.returnToYardline && (
            <>
              <Typography>
                <strong>Return Yardage:</strong> {playLog.returnYardage} yards
              </Typography>
              <Typography>
                <strong>Return To Yardline:</strong> {playLog.returnToYardline}
              </Typography>
            </>
          )}
          {playLog.returner && (
            <Typography>
              <strong>Returner:</strong> {playLog.returner.name}
            </Typography>
          )}
          {playLog.tackle && (
            <Typography>
              <strong>Tackle:</strong>{" "}
              {playLog.tackle.tackler?.name || "Unknown"}
              {playLog.tackle.assisted_tackler && (
                <> (Assisted by: {playLog.tackle.assisted_tackler.name})</>
              )}
            </Typography>
          )}
          {playLog.fumble && (
            <Typography color="error">
              <strong>Fumble:</strong>{" "}
              {playLog.fumble.fumble_player?.name || "Unknown"}
              {playLog.fumble.fumble_recovery_player && (
                <>
                  {" "}
                  - Recovered by: {playLog.fumble.fumble_recovery_player.name}
                </>
              )}
            </Typography>
          )}
          {playLog.interception && (
            <Typography color="error">
              <strong>Interception:</strong>{" "}
              {playLog.interception.interception_player?.name || "Unknown"}
              {playLog.interception.passer && (
                <> - Thrown by: {playLog.interception.passer.name}</>
              )}
            </Typography>
          )}
        </Paper>
      </Grid>
    </Grid>
  </Box>
);

export const PlayDetails: React.FC<PlayDetailsProps> = ({ log }) => {
  if (!log.play_type) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color="error">No play type specified</Typography>
      </Box>
    );
  }

  switch (log.play_type) {
    case "OFFENSE":
      return (
        <OffensivePlayDetails
          playCall={log.play_call as OffensePlayCallLog}
          playLog={log.play_log as OffensivePlayLog}
        />
      );
    case "KICKOFF_RETURN":
      return (
        <KORPlayDetails
          playCall={log.play_call as KORPlayCall}
          playLog={log.play_log as KORPlayLog}
        />
      );
    case "DEFENSE":
      return (
        <DefensivePlayDetails
          playCall={log.play_call as DefensivePlayCall}
          playLog={log.play_log as DefensivePlayLog}
        />
      );
    case "KICKOFF":
      return (
        <KickoffPlayDetails
          playCall={log.play_call as KickoffPlayCall}
          playLog={log.play_log as KickoffPlayLog}
        />
      );
    case "PUNT":
      return (
        <PuntPlayDetails
          playCall={log.play_call as PuntPlayCall}
          playLog={log.play_log as PuntPlayLog}
        />
      );
    case "PUNT_RETURN":
      return (
        <PuntReturnPlayDetails
          playCall={log.play_call as PuntReturnPlayCall}
          playLog={log.play_log as PuntReturnPlayLog}
        />
      );
    case "FIELD_GOAL":
      return (
        <FieldGoalPlayDetails
          playCall={log.play_call as FieldGoalPlayCall}
          playLog={log.play_log as FieldGoalPlayLog}
        />
      );
    case "FIELD_GOAL_BLOCK":
      return (
        <FieldGoalBlockPlayDetails
          playCall={log.play_call as FieldGoalBlockPlayCall}
          playLog={log.play_log as FieldGoalBlockPlayLog}
        />
      );
    default:
      return (
        <Box sx={{ p: 2 }}>
          <Typography color="error">
            Unknown play type: {log.play_type}
          </Typography>
        </Box>
      );
  }
};
